import React, { useState } from 'react';
import ReactDOM from 'react-dom/client';
import { MagnifyingGlassIcon, BoltIcon } from '@heroicons/react/24/outline';

function QuickLookup({ csrfToken, analyzePath }) {
  const [keyword, setKeyword] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!keyword.trim()) {
      alert('Please enter a keyword to analyze');
      return;
    }

    setIsAnalyzing(true);

    try {
      const formData = new FormData();
      formData.append('keyword', keyword.trim());
      formData.append('authenticity_token', csrfToken);

      const response = await fetch(analyzePath, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json',
        },
        body: formData,
      });

      if (response.ok) {
        // Handle JSON response
        const result = await response.json();
        if (result.redirect_url) {
          window.location.href = result.redirect_url;
        }
      } else {
        const errorResult = await response.json().catch(() => ({}));
        throw new Error(errorResult.error || 'Analysis failed');
      }
    } catch (error) {
      console.error('Analysis error:', error);
      alert('Analysis failed. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="text-center mb-6">
        <div className="flex items-center justify-center space-x-2 mb-3">
          <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
            <BoltIcon className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">
              Quick Keyword Lookup
            </h2>
          </div>
        </div>
        <p className="text-gray-400 text-sm max-w-xl mx-auto">
          Instant analysis for any keyword. Get volume, difficulty, AI classification, and monetization insights.
        </p>
      </div>

      {/* Main lookup form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Keyword input */}
        <div className="space-y-2">
          <label className="block text-xs font-medium text-gray-300">
            Keyword to Analyze
          </label>
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="e.g., adhd test, workout planner, recipe finder..."
              className="w-full pl-10 pr-3 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-gray-400 focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all text-sm"
              disabled={isAnalyzing}
            />
          </div>
          <p className="text-xs text-gray-500">
            Enter any keyword phrase to get instant SEO metrics and AI-powered business insights
          </p>
        </div>

        {/* Submit button */}
        <div className="flex justify-center">
          <button
            type="submit"
            disabled={isAnalyzing || !keyword.trim()}
            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 disabled:from-gray-600 disabled:to-gray-700 text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 flex items-center space-x-2 text-sm disabled:cursor-not-allowed"
          >
            {isAnalyzing ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                <span>Analyzing...</span>
              </>
            ) : (
              <>
                <BoltIcon className="w-4 h-4" />
                <span>Analyze Keyword</span>
              </>
            )}
          </button>
        </div>
      </form>

      {/* Info cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-6 pt-6 border-t border-slate-700/50">
        <div className="text-center p-3 bg-slate-700/30 rounded-lg border border-slate-600/30">
          <div className="w-6 h-6 bg-cyan-500/20 rounded-md flex items-center justify-center mx-auto mb-2">
            <svg className="w-3 h-3 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h3 className="font-medium text-white text-xs">Volume Index</h3>
          <p className="text-xs text-gray-400 mt-1">Monthly search volume</p>
        </div>

        <div className="text-center p-3 bg-slate-700/30 rounded-lg border border-slate-600/30">
          <div className="w-6 h-6 bg-yellow-500/20 rounded-md flex items-center justify-center mx-auto mb-2">
            <svg className="w-3 h-3 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <h3 className="font-medium text-white text-xs">Difficulty Rating</h3>
          <p className="text-xs text-gray-400 mt-1">Competition level</p>
        </div>

        <div className="text-center p-3 bg-slate-700/30 rounded-lg border border-slate-600/30">
          <div className="w-6 h-6 bg-purple-500/20 rounded-md flex items-center justify-center mx-auto mb-2">
            <svg className="w-3 h-3 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
          <h3 className="font-medium text-white text-xs">AI Insights</h3>
          <p className="text-xs text-gray-400 mt-1">Business potential</p>
        </div>
      </div>
    </div>
  );
}


export default QuickLookup;